import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { 
  Calendar, Clock, MapPin, User, AlertTriangle, CheckCircle, XCircle, 
  Loader, RefreshCw, ChevronLeft, ChevronRight, Activity, 
  AlertCircle, Wrench, CalendarDays, Timer, X, Shield, FileText
} from 'lucide-react';
import { http } from '../../lib/api';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths, isSameDay, isSameMonth, isToday } from 'date-fns';
import { es } from 'date-fns/locale';

export default function MisAgendamientos() {
  const [agendamientos, setAgendamientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [mesActual, setMesActual] = useState(new Date());
  const [diaSeleccionado, setDiaSeleccionado] = useState(null);
  const [detalleAbierto, setDetalleAbierto] = useState(null);
  const [vistaActiva, setVistaActiva] = useState('calendario'); // 'calendario' | 'pendientes'

  useEffect(() => { cargarUsuario(); }, []);
  useEffect(() => { if (user) cargarMisAgendamientos(); }, [user]);

  const cargarUsuario = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) { const res = await http.get('/auth/me'); setUser(res.data); }
    } catch (err) { console.error('Error cargando usuario:', err); setError('Error al cargar información del usuario'); }
  };

  const cargarMisAgendamientos = async () => {
    setLoading(true);
    try {
      const response = await http.get('/agendamientos/');
      const data = Array.isArray(response.data) ? response.data : [];
      const mis = data.filter(ag => ag.cliente?.id === user.cliente_id || ag.cliente?._id === user.cliente_id || ag.cliente_id === user.cliente_id);
      mis.sort((a, b) => new Date(a.fecha_programada) - new Date(b.fecha_programada));
      setAgendamientos(mis);
    } catch (err) { console.error('Error:', err); setError('Error al cargar agendamientos'); }
    finally { setLoading(false); }
  };

  // Agendamientos pendientes/próximos
  const pendientes = useMemo(() => {
    const hoy = new Date(); hoy.setHours(0,0,0,0);
    return agendamientos
      .filter(ag => ['pendiente', 'confirmado', 'reagendado'].includes(ag.estado) && new Date(ag.fecha_programada) >= hoy)
      .sort((a, b) => new Date(a.fecha_programada) - new Date(b.fecha_programada));
  }, [agendamientos]);

  // Generar días del calendario
  const diasCalendario = useMemo(() => {
    const inicioMes = startOfMonth(mesActual);
    const finMes = endOfMonth(mesActual);
    const dias = eachDayOfInterval({ start: inicioMes, end: finMes });
    // Padding al inicio (lunes = 0)
    const primerDia = getDay(inicioMes);
    const offset = primerDia === 0 ? 6 : primerDia - 1; // Lunes=0
    return { dias, offset };
  }, [mesActual]);

  // Agendamientos por día
  const agendamientosPorDia = useMemo(() => {
    const map = {};
    agendamientos.forEach(ag => {
      if (!ag.fecha_programada) return;
      const key = format(new Date(ag.fecha_programada), 'yyyy-MM-dd');
      if (!map[key]) map[key] = [];
      map[key].push(ag);
    });
    return map;
  }, [agendamientos]);

  // Agendamientos del día seleccionado
  const agendamientosDia = useMemo(() => {
    if (!diaSeleccionado) return [];
    const key = format(diaSeleccionado, 'yyyy-MM-dd');
    return agendamientosPorDia[key] || [];
  }, [diaSeleccionado, agendamientosPorDia]);

  const formatTime = (t) => t ? t.substring(0, 5) : '';

  const puedePostponer = (ag) => {
    if (ag.estado !== 'pendiente' && ag.estado !== 'confirmado') return false;
    const dias = Math.ceil((new Date(ag.fecha_programada) - new Date()) / (1000*60*60*24));
    return dias >= 3;
  };

  const postponerAgendamiento = async (id) => {
    if (!window.confirm('¿Desea posponer este agendamiento? Será reagendado para una fecha posterior.')) return;
    try {
      await http.put('/agendamientos/' + id, { estado: 'reagendado', observaciones: 'Reagendado por cliente el ' + new Date().toLocaleDateString('es-ES') });
      alert('Agendamiento postponido exitosamente.');
      setDetalleAbierto(null);
      cargarMisAgendamientos();
    } catch (e) { alert('Error al postponer. Intente nuevamente.'); }
  };

  const estadoConfig = {
    'pendiente':   { color: 'bg-amber-100 text-amber-800 border-amber-200', dot: 'bg-amber-500', calDot: 'bg-amber-400', icon: Clock },
    'confirmado':  { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', dot: 'bg-emerald-500', calDot: 'bg-emerald-400', icon: CheckCircle },
    'en_progreso': { color: 'bg-violet-100 text-violet-800 border-violet-200', dot: 'bg-violet-500', calDot: 'bg-violet-400', icon: Activity },
    'completado':  { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', dot: 'bg-emerald-500', calDot: 'bg-emerald-400', icon: CheckCircle },
    'cancelado':   { color: 'bg-red-100 text-red-800 border-red-200', dot: 'bg-red-500', calDot: 'bg-red-400', icon: XCircle },
    'reagendado':  { color: 'bg-orange-100 text-orange-800 border-orange-200', dot: 'bg-orange-500', calDot: 'bg-orange-400', icon: AlertTriangle },
  };
  const estadoTexto = { 'pendiente': 'Pendiente', 'confirmado': 'Confirmado', 'en_progreso': 'En Proceso', 'completado': 'Completado', 'cancelado': 'Cancelado', 'reagendado': 'Reagendado' };
  const tipoTexto = { 'preventivo': 'Preventivo', 'correctivo': 'Correctivo', 'revision': 'Revisión', 'limpieza': 'Limpieza', 'actualizacion': 'Actualización' };

  // --- Loading / Error ---
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader className="w-10 h-10 text-emerald-600 animate-spin mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Cargando calendario...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-800 font-medium mb-3">{error}</p>
          <button onClick={cargarMisAgendamientos} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700">
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  // --- Detalle Modal ---
  const DetalleModal = () => {
    if (!detalleAbierto) return null;
    const ag = detalleAbierto;
    const cfg = estadoConfig[ag.estado] || estadoConfig.pendiente;
    const EstadoIcon = cfg.icon;

    return createPortal(
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center" onClick={() => setDetalleAbierto(null)}>
        <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
          <div className="sm:hidden flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className={"inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border " + cfg.color}>
                    <EstadoIcon size={12} />
                    {estadoTexto[ag.estado] || ag.estado}
                  </span>
                </div>
                <h2 className="text-lg font-bold text-gray-900 leading-tight">{ag.titulo}</h2>
              </div>
              <button onClick={() => setDetalleAbierto(null)} className="p-2 -mr-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0">
                <X size={20} className="text-gray-400" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <InfoItem icon={Calendar} color="indigo" label="Fecha" value={ag.fecha_programada ? format(new Date(ag.fecha_programada), "EEEE d 'de' MMMM, yyyy", { locale: es }) : 'Sin fecha'} />
              <InfoItem icon={Clock} color="emerald" label="Horario" value={formatTime(ag.hora_inicio) + ' - ' + formatTime(ag.hora_fin)} />
              <InfoItem icon={MapPin} color="rose" label="Ubicación" value={ag.ubicacion || 'No especificada'} sub={ag.sucursal ? 'Sucursal: ' + ag.sucursal : null} />
              <InfoItem icon={User} color="purple" label="Técnico" value={ag.tecnico_asignado || 'Por asignar'} />
            </div>
            <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 rounded-xl">
              <Wrench size={16} className="text-gray-500" />
              <span className="text-sm text-gray-600">Tipo:</span>
              <span className="text-sm font-semibold text-gray-900">{tipoTexto[ag.tipo_mantenimiento] || ag.tipo_mantenimiento}</span>
            </div>
            {ag.descripcion && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <FileText size={12} /> Descripción
                </h4>
                <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-xl p-3">{ag.descripcion}</p>
              </div>
            )}
            {ag.observaciones && (
              <div>
                <h4 className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <AlertTriangle size={12} /> Observaciones
                </h4>
                <p className="text-sm text-amber-800 leading-relaxed bg-amber-50 border border-amber-100 rounded-xl p-3">{ag.observaciones}</p>
              </div>
            )}
            {ag.equipos && ag.equipos.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Shield size={12} /> Equipos ({ag.equipos.length})
                </h4>
                <div className="space-y-2">
                  {ag.equipos.map((eq, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2.5 bg-white border border-gray-200 rounded-xl">
                      <span className="text-sm font-medium text-gray-900">{eq.nombre}</span>
                      <span className="text-xs text-gray-400">{eq.marca} {eq.modelo}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {puedePostponer(ag) && (
            <div className="border-t border-gray-100 px-5 py-4">
              <button
                onClick={() => postponerAgendamiento(ag.id || ag._id)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-orange-600 text-white rounded-xl hover:bg-orange-700 active:scale-[0.98] transition-all text-sm font-semibold"
              >
                <Timer size={16} /> Postponer Agendamiento
              </button>
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  };

  // --- Main Render ---
  const diasSemana = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

  return (
    <div className="bg-gray-50 min-h-screen pb-20">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-gray-900">Mi Calendario</h1>
              <p className="text-xs text-gray-500 hidden sm:block">Visitas programadas y agendamientos</p>
            </div>
            <button onClick={cargarMisAgendamientos} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors active:scale-95">
              <RefreshCw size={18} />
            </button>
          </div>
          {/* Tabs */}
          <div className="flex gap-1 mt-3 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setVistaActiva('calendario')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-sm font-medium transition-colors ${vistaActiva === 'calendario' ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <CalendarDays size={15} /> Calendario
            </button>
            <button
              onClick={() => setVistaActiva('pendientes')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-sm font-medium transition-colors ${vistaActiva === 'pendientes' ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <Clock size={15} /> Pendientes
              {pendientes.length > 0 && (
                <span className="bg-amber-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">{pendientes.length}</span>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-4">
        {vistaActiva === 'calendario' ? (
          <div className="space-y-4">
            {/* Navegación del mes */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-4">
                <button onClick={() => setMesActual(prev => subMonths(prev, 1))} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <ChevronLeft size={20} className="text-gray-600" />
                </button>
                <h2 className="text-lg font-bold text-gray-900 capitalize">
                  {format(mesActual, 'MMMM yyyy', { locale: es })}
                </h2>
                <button onClick={() => setMesActual(prev => addMonths(prev, 1))} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <ChevronRight size={20} className="text-gray-600" />
                </button>
              </div>

              {/* Grid del calendario */}
              <div className="grid grid-cols-7 gap-px">
                {/* Header días semana */}
                {diasSemana.map(dia => (
                  <div key={dia} className="text-center text-xs font-semibold text-gray-500 py-2">{dia}</div>
                ))}
                {/* Espacios vacíos al inicio */}
                {Array.from({ length: diasCalendario.offset }).map((_, i) => (
                  <div key={`empty-${i}`} className="min-h-[60px] sm:min-h-[80px]" />
                ))}
                {/* Días del mes */}
                {diasCalendario.dias.map(dia => {
                  const key = format(dia, 'yyyy-MM-dd');
                  const ags = agendamientosPorDia[key] || [];
                  const esHoy = isToday(dia);
                  const seleccionado = diaSeleccionado && isSameDay(dia, diaSeleccionado);

                  return (
                    <button
                      key={key}
                      onClick={() => setDiaSeleccionado(seleccionado ? null : dia)}
                      className={`min-h-[60px] sm:min-h-[80px] p-1 border rounded-lg text-left transition-all relative
                        ${esHoy ? 'border-emerald-300 bg-emerald-50/50' : 'border-gray-100 hover:border-gray-300'}
                        ${seleccionado ? 'ring-2 ring-emerald-500 border-emerald-400 bg-emerald-50' : ''}
                        ${ags.length > 0 ? 'hover:shadow-md' : 'hover:bg-gray-50'}`}
                    >
                      <span className={`text-sm font-medium ${esHoy ? 'bg-emerald-600 text-white w-6 h-6 rounded-full flex items-center justify-center' : 'text-gray-700'}`}>
                        {format(dia, 'd')}
                      </span>
                      {ags.length > 0 && (
                        <div className="flex flex-wrap gap-0.5 mt-1">
                          {ags.slice(0, 3).map((ag, i) => {
                            const cfg = estadoConfig[ag.estado] || estadoConfig.pendiente;
                            return (
                              <div key={i} className={`w-2 h-2 rounded-full ${cfg.calDot}`} title={ag.titulo} />
                            );
                          })}
                          {ags.length > 3 && (
                            <span className="text-[9px] text-gray-400 font-medium">+{ags.length - 3}</span>
                          )}
                        </div>
                      )}
                      {/* Mini label on desktop */}
                      {ags.length > 0 && (
                        <div className="hidden sm:block mt-0.5">
                          <span className="text-[10px] text-gray-500 truncate block">{ags[0].titulo}</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Leyenda */}
              <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t border-gray-100 justify-center">
                {[['pendiente', 'Pendiente'], ['confirmado', 'Confirmado'], ['completado', 'Completado'], ['en_progreso', 'En proceso']].map(([key, label]) => (
                  <span key={key} className="flex items-center gap-1.5 text-xs text-gray-500">
                    <span className={`w-2.5 h-2.5 rounded-full ${estadoConfig[key].calDot}`} />
                    {label}
                  </span>
                ))}
              </div>
            </div>

            {/* Detalle del día seleccionado */}
            {diaSeleccionado && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900 capitalize">
                    {format(diaSeleccionado, "EEEE d 'de' MMMM, yyyy", { locale: es })}
                  </h3>
                  <p className="text-xs text-gray-500">{agendamientosDia.length} {agendamientosDia.length === 1 ? 'visita' : 'visitas'}</p>
                </div>
                {agendamientosDia.length === 0 ? (
                  <div className="p-6 text-center text-gray-400 text-sm">Sin visitas programadas para este día</div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {agendamientosDia.map(ag => {
                      const cfg = estadoConfig[ag.estado] || estadoConfig.pendiente;
                      return (
                        <button
                          key={ag.id || ag._id}
                          onClick={() => setDetalleAbierto(ag)}
                          className="w-full text-left px-5 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3"
                        >
                          <div className={`w-2 h-full min-h-[40px] rounded-full flex-shrink-0 ${cfg.dot}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{ag.titulo}</p>
                            <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                              <span className="flex items-center gap-1"><Clock size={11} /> {formatTime(ag.hora_inicio)} - {formatTime(ag.hora_fin)}</span>
                              {ag.tecnico_asignado && <span className="flex items-center gap-1"><User size={11} /> {ag.tecnico_asignado}</span>}
                            </div>
                          </div>
                          <span className={"text-[10px] font-semibold px-2 py-0.5 rounded-full border " + cfg.color}>
                            {estadoTexto[ag.estado] || ag.estado}
                          </span>
                          <ChevronRight size={14} className="text-gray-300 flex-shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Vista de pendientes */
          <div className="space-y-3">
            {pendientes.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
                <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">No tiene agendamientos pendientes</p>
                <p className="text-xs text-gray-400 mt-1">Todas sus visitas están al día</p>
              </div>
            ) : (
              pendientes.map((ag) => {
                const cfg = estadoConfig[ag.estado] || estadoConfig.pendiente;
                const diasRestantes = Math.ceil((new Date(ag.fecha_programada) - new Date()) / (1000*60*60*24));
                return (
                  <button
                    key={ag.id || ag._id}
                    onClick={() => setDetalleAbierto(ag)}
                    className="w-full text-left bg-white rounded-2xl border border-gray-200 p-4 transition-all active:scale-[0.99] hover:shadow-md hover:border-emerald-200 group"
                  >
                    <div className="flex items-start gap-3">
                      {/* Date badge */}
                      <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-emerald-50 text-emerald-700 flex flex-col items-center justify-center">
                        <span className="text-xl font-bold leading-none">
                          {(() => { try { return format(new Date(ag.fecha_programada), 'd'); } catch { return '?'; } })()}
                        </span>
                        <span className="text-[10px] uppercase font-semibold leading-none mt-0.5">
                          {(() => { try { return format(new Date(ag.fecha_programada), 'MMM', { locale: es }); } catch { return ''; } })()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-sm font-semibold text-gray-900 truncate">{ag.titulo}</h3>
                          {diasRestantes <= 3 && (
                            <span className="text-[10px] bg-red-100 text-red-700 font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0">
                              {diasRestantes === 0 ? 'Hoy' : diasRestantes === 1 ? 'Mañana' : `En ${diasRestantes} días`}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1"><Clock size={11} /> {formatTime(ag.hora_inicio)}{ag.hora_fin ? ' - ' + formatTime(ag.hora_fin) : ''}</span>
                          {ag.tecnico_asignado && <span className="flex items-center gap-1 truncate"><User size={11} /> {ag.tecnico_asignado}</span>}
                        </div>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className={"inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border " + cfg.color}>
                            <span className={"w-1.5 h-1.5 rounded-full " + cfg.dot} />
                            {estadoTexto[ag.estado] || ag.estado}
                          </span>
                          <span className="text-[10px] text-gray-400 font-medium">{tipoTexto[ag.tipo_mantenimiento] || ag.tipo_mantenimiento}</span>
                          {ag.ubicacion && <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><MapPin size={9}/> {ag.ubicacion}</span>}
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-gray-300 group-hover:text-emerald-500 transition-colors flex-shrink-0 mt-1" />
                    </div>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>

      <DetalleModal />
    </div>
  );
}

function InfoItem({ icon: Icon, color, label, value, sub }) {
  const colors = {
    indigo: 'bg-teal-50 text-teal-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    rose: 'bg-rose-50 text-rose-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  return (
    <div className="flex items-start gap-2.5">
      <div className={"p-1.5 rounded-lg flex-shrink-0 mt-0.5 " + colors[color]}>
        <Icon size={14} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{label}</p>
        <p className="text-sm text-gray-900 font-medium leading-snug capitalize">{value}</p>
        {sub && <p className="text-xs text-gray-500">{sub}</p>}
      </div>
    </div>
  );
}
